import { useCallback, useEffect, useMemo, useState } from "react";
import {
  EMPTY_NAVIGATION_TARGETS,
  SINGLE_ITEM_ZOOM_PADDING_PDF_POINTS,
  STATUS_RGB,
} from "../../constants";
import {
  buildMarkerNavigationTargets,
  buildRouteNavigationTargets,
} from "../../utils";
import type { NavigableObject } from "components/SmartSurveyor/ReviewPage/ObjectNavigationBar/types";
import type { UseVisualDiffNavigation } from "./types";

export const useVisualDiffNavigation: UseVisualDiffNavigation = ({
  visualDiff,
  activeKind,
  selectedTypeKey,
  visibleStatuses,
  visibleRoutes,
  detectedObjects,
  viewerRef,
}) => {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const navigationTargets = useMemo(() => {
    if (visualDiff === null || selectedTypeKey === null) {
      return EMPTY_NAVIGATION_TARGETS;
    }
    if (activeKind === "elements") {
      const markers = visualDiff.markersByType.get(selectedTypeKey) ?? [];
      return buildMarkerNavigationTargets(markers, visibleStatuses);
    }
    const diffRoutes = visualDiff.routesByType.get(selectedTypeKey) ?? [];
    return buildRouteNavigationTargets(diffRoutes, visibleStatuses);
  }, [visualDiff, selectedTypeKey, activeKind, visibleStatuses]);

  const selectedObject = useMemo(() => {
    if (selectedItemId === null) return null;
    if (activeKind === "elements") {
      return (
        detectedObjects.find((object) => object.id === selectedItemId) ?? null
      );
    }
    const selectedDiffRoute = visibleRoutes.find(
      (diffRoute) => diffRoute.route.id === selectedItemId,
    );
    if (selectedDiffRoute === undefined) return null;
    // A route has no bbox of its own, so its extents stand in as one.
    const { coordinateExtents } = selectedDiffRoute.route;
    return {
      id: selectedItemId,
      rgb: STATUS_RGB[selectedDiffRoute.status],
      bbox: {
        x1: coordinateExtents.minX,
        y1: coordinateExtents.minY,
        x2: coordinateExtents.maxX,
        y2: coordinateExtents.maxY,
      },
      status: selectedDiffRoute.status,
    };
  }, [selectedItemId, activeKind, detectedObjects, visibleRoutes]);

  const handleAfterNavigate = useCallback(
    (object: NavigableObject) => {
      const target = navigationTargets.find(
        (navigationTarget) => navigationTarget.id === object.id,
      );
      if (target === undefined || target.zoomTargets.length === 0) return;
      viewerRef.current?.zoomToElements({
        elements: target.zoomTargets,
        padding: SINGLE_ITEM_ZOOM_PADDING_PDF_POINTS,
      });
    },
    [navigationTargets, viewerRef],
  );

  // Keeps the traversal cursor on an item that is actually on screen.
  useEffect(() => {
    if (navigationTargets.length === 0) {
      setSelectedItemId(null);
      return;
    }
    const isSelectedStillVisible = navigationTargets.some(
      (target) => target.id === selectedItemId,
    );
    if (isSelectedStillVisible) return;
    setSelectedItemId(navigationTargets[0].id);
  }, [navigationTargets, selectedItemId]);

  return {
    navigationTargets,
    selectedObject,
    selectItemId: setSelectedItemId,
    handleAfterNavigate,
  };
};
